/**
 * Support Chat Widget
 *
 * Floating chat widget for user support powered by Claude AI.
 * - Collects user context (page, errors, etc.)
 * - Sends messages to /api/support/chat
 * - Screenshot capture with annotation
 * - Console log capture for debugging
 * - Never exposes code or internal details
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { Icons } from '../Icons';
import { useAuthStore } from '../../store/authStore';
import { useHybridStorage } from '../../hooks/useHybridStorage';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  screenshot?: string; // Base64 screenshot image
}

interface SupportContext {
  page: string;
  userAgent: string;
  userId?: string;
  mode?: 'local' | 'cloud';
  errors?: string[];
  warnings?: string[];
  logs?: string[];
  timestamp: string;
  language: string; // Browser language (fr, en, etc.)
  screenshot?: string; // Base64 screenshot
  screenSize?: { width: number; height: number };
  localStorage?: Record<string, string>;
}

// Capture console errors, warnings and logs for context
const consoleErrors: string[] = [];
const consoleWarnings: string[] = [];
const consoleLogs: string[] = [];
const MAX_ENTRIES = 20;

// Override console.error to capture errors
const originalConsoleError = console.error;
console.error = (...args: unknown[]) => {
  const errorMsg = args.map(a =>
    typeof a === 'object' ? JSON.stringify(a) : String(a)
  ).join(' ');

  if (consoleErrors.length >= MAX_ENTRIES) {
    consoleErrors.shift();
  }
  consoleErrors.push(`[${new Date().toISOString()}] ${errorMsg.slice(0, 500)}`);

  originalConsoleError.apply(console, args);
};

// Override console.warn to capture warnings
const originalConsoleWarn = console.warn;
console.warn = (...args: unknown[]) => {
  const warnMsg = args.map(a =>
    typeof a === 'object' ? JSON.stringify(a) : String(a)
  ).join(' ');

  if (consoleWarnings.length >= MAX_ENTRIES) {
    consoleWarnings.shift();
  }
  consoleWarnings.push(`[${new Date().toISOString()}] ${warnMsg.slice(0, 500)}`);

  originalConsoleWarn.apply(console, args);
};

// Override console.log to capture logs (only important ones)
const originalConsoleLog = console.log;
console.log = (...args: unknown[]) => {
  const logMsg = args.map(a =>
    typeof a === 'object' ? JSON.stringify(a) : String(a)
  ).join(' ');

  // Only capture logs that might be useful for debugging
  if (logMsg.includes('Error') || logMsg.includes('error') ||
      logMsg.includes('fail') || logMsg.includes('Fail') ||
      logMsg.includes('[') || logMsg.includes('API')) {
    if (consoleLogs.length >= MAX_ENTRIES) {
      consoleLogs.shift();
    }
    consoleLogs.push(`[${new Date().toISOString()}] ${logMsg.slice(0, 500)}`);
  }

  originalConsoleLog.apply(console, args);
};

// Generate unique session ID (persists for browser session)
function getSessionId(): string {
  const key = 'support_session_id';
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
}

// Annotation drawing state
interface DrawPoint {
  x: number;
  y: number;
}

interface DrawPath {
  points: DrawPoint[];
  color: string;
  width: number;
}

export function SupportChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [hintDismissed, setHintDismissed] = useState(() => {
    // Check if user has dismissed the hint in this session
    return sessionStorage.getItem('support_hint_dismissed') === 'true';
  });

  // Screenshot and annotation state
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturedScreenshot, setCapturedScreenshot] = useState<string | null>(null);
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [annotationPaths, setAnnotationPaths] = useState<DrawPath[]>([]);
  const [currentPath, setCurrentPath] = useState<DrawPath | null>(null);
  const [annotationColor, setAnnotationColor] = useState('#ef4444'); // Red
  const [pendingScreenshot, setPendingScreenshot] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sessionIdRef = useRef<string>(getSessionId());
  const annotationCanvasRef = useRef<HTMLCanvasElement>(null);
  const screenshotImageRef = useRef<HTMLImageElement>(null);

  // Get user context
  const { user } = useAuthStore();
  const { mode } = useHybridStorage();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Get browser language (fr, en, etc.)
  const getBrowserLanguage = (): string => {
    const lang = navigator.language || (navigator as any).userLanguage || 'en';
    return lang.split('-')[0].toLowerCase(); // "fr-FR" -> "fr"
  };

  // Get relevant localStorage keys for debugging
  const getRelevantLocalStorage = (): Record<string, string> => {
    const relevant: Record<string, string> = {};
    const importantKeys = ['hybrid_storage_mode', 'theme', 'language', 'lastVisit'];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (importantKeys.some(k => key.includes(k)) || key.startsWith('app_'))) {
          const value = localStorage.getItem(key);
          if (value && value.length < 200) { // Don't include large values
            relevant[key] = value;
          }
        }
      }
    } catch {
      // Ignore errors
    }
    return relevant;
  };

  // Build context for API
  const buildContext = useCallback((screenshot?: string): SupportContext => {
    return {
      page: window.location.pathname,
      userAgent: navigator.userAgent,
      userId: user?.id,
      mode,
      errors: consoleErrors.slice(-10), // Last 10 errors
      warnings: consoleWarnings.slice(-5), // Last 5 warnings
      logs: consoleLogs.slice(-10), // Last 10 important logs
      timestamp: new Date().toISOString(),
      language: getBrowserLanguage(),
      screenshot,
      screenSize: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      localStorage: getRelevantLocalStorage(),
    };
  }, [user?.id, mode]);

  // Capture screenshot of the page
  const captureScreenshot = async (): Promise<void> => {
    try {
      setIsCapturing(true);
      setIsOpen(false); // Hide chat widget during capture

      // Wait for UI to update
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        allowTaint: true,
        scale: 0.5, // Reduce size
        ignoreElements: (element) => {
          // Ignore the chat widget and any modals during capture
          return element.getAttribute('data-support-chat') === 'true';
        },
      });

      const screenshot = canvas.toDataURL('image/jpeg', 0.7);
      setCapturedScreenshot(screenshot);
      setIsAnnotating(true);
      setAnnotationPaths([]);
    } catch (err) {
      console.error('[Support] Screenshot capture error:', err);
      setError(getBrowserLanguage() === 'fr'
        ? 'Erreur lors de la capture d\'écran'
        : 'Screenshot capture error');
      setIsOpen(true);
    } finally {
      setIsCapturing(false);
    }
  };

  // Redraw annotation canvas
  const redrawAnnotationCanvas = useCallback(() => {
    const canvas = annotationCanvasRef.current;
    const img = screenshotImageRef.current;
    if (!canvas || !img || !capturedScreenshot) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear and redraw image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Draw all paths
    [...annotationPaths, currentPath].filter(Boolean).forEach(path => {
      if (!path || path.points.length < 2) return;

      ctx.beginPath();
      ctx.strokeStyle = path.color;
      ctx.lineWidth = path.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.moveTo(path.points[0].x, path.points[0].y);
      for (let i = 1; i < path.points.length; i++) {
        ctx.lineTo(path.points[i].x, path.points[i].y);
      }
      ctx.stroke();
    });
  }, [capturedScreenshot, annotationPaths, currentPath]);

  // Setup annotation canvas when screenshot is captured
  useEffect(() => {
    if (capturedScreenshot && isAnnotating && annotationCanvasRef.current) {
      const img = new Image();
      img.onload = () => {
        screenshotImageRef.current = img;
        const canvas = annotationCanvasRef.current;
        if (canvas) {
          // Set canvas size to match container while maintaining aspect ratio
          const maxWidth = Math.min(window.innerWidth - 48, 800);
          const maxHeight = window.innerHeight - 200;
          const scale = Math.min(maxWidth / img.width, maxHeight / img.height);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          redrawAnnotationCanvas();
        }
      };
      img.src = capturedScreenshot;
    }
  }, [capturedScreenshot, isAnnotating, redrawAnnotationCanvas]);

  // Annotation mouse/touch handlers
  const getCanvasPoint = (e: React.MouseEvent | React.TouchEvent): DrawPoint | null => {
    const canvas = annotationCanvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    let clientX: number, clientY: number;

    if ('touches' in e) {
      if (e.touches.length === 0) return null;
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const handleAnnotationStart = (e: React.MouseEvent | React.TouchEvent) => {
    const point = getCanvasPoint(e);
    if (!point) return;

    setCurrentPath({
      points: [point],
      color: annotationColor,
      width: 4,
    });
  };

  const handleAnnotationMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!currentPath) return;
    const point = getCanvasPoint(e);
    if (!point) return;

    setCurrentPath(prev => {
      if (!prev) return null;
      return {
        ...prev,
        points: [...prev.points, point],
      };
    });

    redrawAnnotationCanvas();
  };

  const handleAnnotationEnd = () => {
    if (currentPath && currentPath.points.length > 1) {
      setAnnotationPaths(prev => [...prev, currentPath]);
    }
    setCurrentPath(null);
  };

  // Confirm annotation and attach to message
  const confirmAnnotation = () => {
    const canvas = annotationCanvasRef.current;
    if (!canvas) return;

    const annotatedScreenshot = canvas.toDataURL('image/jpeg', 0.8);
    setPendingScreenshot(annotatedScreenshot);
    setIsAnnotating(false);
    setCapturedScreenshot(null);
    setAnnotationPaths([]);
    setIsOpen(true);
  };

  // Cancel annotation
  const cancelAnnotation = () => {
    setIsAnnotating(false);
    setCapturedScreenshot(null);
    setAnnotationPaths([]);
    setIsOpen(true);
  };

  // Undo last annotation
  const undoAnnotation = () => {
    setAnnotationPaths(prev => prev.slice(0, -1));
    setTimeout(redrawAnnotationCanvas, 0);
  };

  // Clear pending screenshot
  const clearPendingScreenshot = () => {
    setPendingScreenshot(null);
  };

  // Send message to API
  const sendMessage = async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    setError(null);
    setInputValue('');

    // Capture pending screenshot
    const screenshotToSend = pendingScreenshot;
    setPendingScreenshot(null);

    // Add user message with screenshot if present
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: trimmedInput,
      timestamp: new Date(),
      screenshot: screenshotToSend || undefined,
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await fetch('/api/support/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmedInput,
          history: messages.map(m => ({ role: m.role, content: m.content })),
          context: buildContext(screenshotToSend || undefined),
          sessionId: sessionIdRef.current,
          conversationId: conversationId,
          screenshot: screenshotToSend,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur de communication');
      }

      // Track conversation ID from backend
      if (data.conversationId && !conversationId) {
        setConversationId(data.conversationId);
      }

      // Add assistant message
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.message,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key (Shift+Enter for new line)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Welcome messages by language
  const getWelcomeMessage = (lang: string): string => {
    const messages: Record<string, string> = {
      fr: 'Bonjour ! Je suis l\'assistant support de Edouard.ai. Comment puis-je vous aider aujourd\'hui ?\n\nJe peux vous aider avec :\n- Questions sur l\'utilisation de l\'app\n- Signalement de bugs\n- Suggestions d\'amélioration',
      en: 'Hello! I\'m the Edouard.ai support assistant. How can I help you today?\n\nI can help you with:\n- Questions about using the app\n- Bug reports\n- Feature suggestions',
      es: '¡Hola! Soy el asistente de soporte de Edouard.ai. ¿Cómo puedo ayudarte hoy?\n\nPuedo ayudarte con:\n- Preguntas sobre el uso de la app\n- Reportes de errores\n- Sugerencias de mejora',
      de: 'Hallo! Ich bin der Support-Assistent von Edouard.ai. Wie kann ich Ihnen heute helfen?\n\nIch kann Ihnen helfen bei:\n- Fragen zur Nutzung der App\n- Fehlermeldungen\n- Verbesserungsvorschläge',
    };
    return messages[lang] || messages.en;
  };

  // Welcome message when opening for first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const lang = getBrowserLanguage();
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: getWelcomeMessage(lang),
        timestamp: new Date(),
      }]);
    }
  }, [isOpen, messages.length]);

  return (
    <>
      {/* Floating hint message when chat is closed */}
      {!isOpen && !hintDismissed && (
        <div
          style={{
            position: 'fixed',
            bottom: '90px',
            right: '24px',
            maxWidth: '220px',
            padding: '10px 14px',
            paddingRight: '32px',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%)',
            borderRadius: '12px 12px 4px 12px',
            boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)',
            zIndex: 9997,
            animation: 'fadeInUp 0.5s ease-out',
          }}
        >
          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setHintDismissed(true);
              sessionStorage.setItem('support_hint_dismissed', 'true');
            }}
            style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            aria-label={getBrowserLanguage() === 'fr' ? 'Fermer' : 'Close'}
          >
            <Icons.X size={12} color="white" />
          </button>
          <div style={{ color: 'white', fontSize: '12px', lineHeight: '1.4' }}>
            <span style={{ fontSize: '14px' }}>🚧</span>{' '}
            {getBrowserLanguage() === 'fr'
              ? 'App en développement ! Signalez-nous les bugs, ça nous aide énormément 🙏'
              : 'App in development! Report bugs to help us improve 🙏'}
          </div>
          <div
            style={{
              position: 'absolute',
              bottom: '-6px',
              right: '28px',
              width: '12px',
              height: '12px',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%)',
              transform: 'rotate(45deg)',
            }}
          />
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-support-chat="true"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          border: 'none',
          boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          zIndex: 9998,
          overflow: 'visible',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 25px rgba(99, 102, 241, 0.5)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(99, 102, 241, 0.4)';
        }}
        aria-label={isOpen ? 'Fermer le chat' : 'Ouvrir le chat support'}
      >
        {isOpen ? (
          <Icons.X size={24} color="white" />
        ) : (
          <Icons.MessageCircle size={24} color="white" />
        )}
        {/* Online indicator */}
        <span
          style={{
            position: 'absolute',
            top: '-2px',
            right: '-2px',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            background: '#22c55e',
            border: '3px solid white',
            boxShadow: '0 0 8px rgba(34, 197, 94, 0.6)',
          }}
        />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '96px',
            right: '24px',
            width: '380px',
            maxWidth: 'calc(100vw - 48px)',
            height: '600px',
            maxHeight: 'calc(100vh - 140px)',
            background: 'var(--card-bg, #1a1a2e)',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 9999,
            border: '1px solid var(--border, rgba(255,255,255,0.1))',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '16px 20px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icons.Bot size={22} color="white" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'white', fontWeight: 600, fontSize: '15px' }}>
                Support Edouard.ai
              </div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                Propulsé par Edouard ;)
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label="Fermer"
            >
              <Icons.X size={18} color="white" />
            </button>
          </div>

          {/* Messages Area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {messages.map(message => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '85%',
                    padding: '12px 16px',
                    borderRadius: message.role === 'user'
                      ? '16px 16px 4px 16px'
                      : '16px 16px 16px 4px',
                    background: message.role === 'user'
                      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                      : 'var(--hover-bg, rgba(255,255,255,0.05))',
                    color: message.role === 'user' ? 'white' : 'var(--text-primary, #e0e0e0)',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {/* Screenshot attachment */}
                  {message.screenshot && (
                    <div style={{ marginBottom: '8px' }}>
                      <img
                        src={message.screenshot}
                        alt="Screenshot"
                        style={{
                          maxWidth: '100%',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.2)',
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(message.screenshot, '_blank')}
                      />
                    </div>
                  )}
                  {message.content}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div
                  style={{
                    padding: '12px 16px',
                    borderRadius: '16px 16px 16px 4px',
                    background: 'var(--hover-bg, rgba(255,255,255,0.05))',
                    display: 'flex',
                    gap: '6px',
                  }}
                >
                  <span className="typing-dot" style={{ animationDelay: '0s' }}>•</span>
                  <span className="typing-dot" style={{ animationDelay: '0.2s' }}>•</span>
                  <span className="typing-dot" style={{ animationDelay: '0.4s' }}>•</span>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#ef4444',
                  fontSize: '13px',
                  textAlign: 'center',
                }}
              >
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Pending Screenshot Preview */}
          {pendingScreenshot && (
            <div
              style={{
                padding: '8px 16px',
                borderTop: '1px solid var(--border, rgba(255,255,255,0.1))',
                background: 'rgba(99, 102, 241, 0.1)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img
                  src={pendingScreenshot}
                  alt="Screenshot"
                  style={{
                    width: '60px',
                    height: '40px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    border: '1px solid rgba(99, 102, 241, 0.5)',
                  }}
                />
                <span style={{ flex: 1, fontSize: '12px', color: 'var(--text-secondary, #888)' }}>
                  {getBrowserLanguage() === 'fr' ? 'Capture d\'écran jointe' : 'Screenshot attached'}
                </span>
                <button
                  onClick={clearPendingScreenshot}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-label="Remove screenshot"
                >
                  <Icons.X size={16} color="var(--text-secondary, #888)" />
                </button>
              </div>
            </div>
          )}

          {/* Input Area */}
          <div
            style={{
              padding: '12px 16px',
              borderTop: '1px solid var(--border, rgba(255,255,255,0.1))',
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-end',
            }}
          >
            {/* Screenshot Button */}
            <button
              onClick={captureScreenshot}
              disabled={isLoading || isCapturing}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                border: 'none',
                background: 'var(--hover-bg, rgba(255,255,255,0.1))',
                cursor: isLoading || isCapturing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                flexShrink: 0,
              }}
              title={getBrowserLanguage() === 'fr' ? 'Capturer l\'écran' : 'Capture screenshot'}
              aria-label={getBrowserLanguage() === 'fr' ? 'Capturer l\'écran' : 'Capture screenshot'}
            >
              <Icons.Camera size={20} color="var(--text-secondary, #888)" />
            </button>

            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={getBrowserLanguage() === 'fr' ? 'Écrivez votre message...' : 'Write your message...'}
              disabled={isLoading}
              rows={1}
              style={{
                flex: 1,
                padding: '12px 14px',
                borderRadius: '12px',
                border: '1px solid var(--border, rgba(255,255,255,0.1))',
                background: 'var(--input-bg, rgba(255,255,255,0.05))',
                color: 'var(--text-primary, #e0e0e0)',
                fontSize: '14px',
                resize: 'none',
                maxHeight: '120px',
                outline: 'none',
                fontFamily: 'inherit',
              }}
              onInput={e => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 120) + 'px';
              }}
            />
            <button
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                border: 'none',
                background: inputValue.trim() && !isLoading
                  ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                  : 'var(--hover-bg, rgba(255,255,255,0.1))',
                cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                flexShrink: 0,
              }}
              aria-label="Envoyer"
            >
              <Icons.Send
                size={20}
                color={inputValue.trim() && !isLoading ? 'white' : 'var(--text-secondary, #888)'}
              />
            </button>
          </div>
        </div>
      )}

      {/* Annotation Overlay Modal */}
      {isAnnotating && capturedScreenshot && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          {/* Toolbar */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '16px',
              padding: '12px 20px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '12px',
              alignItems: 'center',
            }}
          >
            <span style={{ color: 'white', fontSize: '14px', marginRight: '8px' }}>
              {getBrowserLanguage() === 'fr' ? 'Dessinez pour annoter :' : 'Draw to annotate:'}
            </span>

            {/* Color picker */}
            {['#ef4444', '#22c55e', '#3b82f6', '#eab308', '#ffffff'].map(color => (
              <button
                key={color}
                onClick={() => setAnnotationColor(color)}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  border: annotationColor === color ? '3px solid white' : '2px solid rgba(255,255,255,0.3)',
                  background: color,
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              />
            ))}

            {/* Undo button */}
            <button
              onClick={undoAnnotation}
              disabled={annotationPaths.length === 0}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: annotationPaths.length > 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.05)',
                color: annotationPaths.length > 0 ? 'white' : 'rgba(255,255,255,0.3)',
                cursor: annotationPaths.length > 0 ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Icons.Undo2 size={16} />
              {getBrowserLanguage() === 'fr' ? 'Annuler' : 'Undo'}
            </button>
          </div>

          {/* Canvas */}
          <canvas
            ref={annotationCanvasRef}
            onMouseDown={handleAnnotationStart}
            onMouseMove={handleAnnotationMove}
            onMouseUp={handleAnnotationEnd}
            onMouseLeave={handleAnnotationEnd}
            onTouchStart={handleAnnotationStart}
            onTouchMove={handleAnnotationMove}
            onTouchEnd={handleAnnotationEnd}
            style={{
              borderRadius: '8px',
              cursor: 'crosshair',
              touchAction: 'none',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            }}
          />

          {/* Action buttons */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              marginTop: '16px',
            }}
          >
            <button
              onClick={cancelAnnotation}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.3)',
                background: 'transparent',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {getBrowserLanguage() === 'fr' ? 'Annuler' : 'Cancel'}
            </button>
            <button
              onClick={confirmAnnotation}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {getBrowserLanguage() === 'fr' ? 'Joindre au message' : 'Attach to message'}
            </button>
          </div>

          {/* Instructions */}
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '12px' }}>
            {getBrowserLanguage() === 'fr'
              ? 'Utilisez votre souris ou doigt pour entourer/souligner les éléments importants'
              : 'Use your mouse or finger to circle/underline important elements'}
          </p>
        </div>
      )}

      {/* Capturing indicator */}
      {isCapturing && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(99, 102, 241, 0.1)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              padding: '20px 40px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: '12px',
              color: 'white',
              fontSize: '16px',
              fontWeight: 500,
              boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)',
            }}
          >
            {getBrowserLanguage() === 'fr' ? 'Capture en cours...' : 'Capturing...'}
          </div>
        </div>
      )}

      {/* Typing animation styles */}
      <style>{`
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        .typing-dot {
          display: inline-block;
          color: var(--text-secondary, #888);
          animation: typingBounce 1.4s ease-in-out infinite;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
}
